/**
 * commands.ts - Game Command Definitions & Handlers
 */

import { CommandExecutor } from '../core/Command.js';
import type { ICommand } from '../core/Command.js';
import type { GameStateData, Floor, Room, GhostInstance, FacilityInstance, AdBoost, RoomProp, LobbyFloor, LobbyProp, FacilityFloor, FacilityProp, DayPhase, GhostStaffType, GhostStaff, LobbyNpcGhost, HauntedThemeId, WorldScreen } from './state.js';
import { GHOST_STAFF_DEFS, applyHauntedBuildingSelection, cloneBuildingStateFromCurrent, configureHauntedBuildingMeta, createInitialHauntedBuildingState, getHauntedBuildingMeta, getMaxFloorsForSelectedBuilding, mapStateToSelectedBuilding, syncSelectedMetaFromBuilding, unlockHauntedBuildingMeta } from './state.js';
import type { RoomTypeId } from './data/rooms.js';
import type { GhostId } from './data/ghosts.js';
import type { TrainingId } from './data/trainings.js';
import type { PropId } from './data/props.js';
import type { FacilityId } from './data/facilities.js';
import type { AcademyTrainingId } from './data/academyTrainings.js';
// LobbyRoomType kept for backward compat (legacy BUILD_LOBBY_FLOOR still references it in type)
import type { LobbyRoomType } from './data/lobbyRooms.js';
import type { LobbyPropId } from './data/lobbyProps.js';
import type { FacilityPropId } from './data/facilityProps.js';
import type { MainView } from './state.js';
import { GHOST_DEFS } from './data/ghosts.js';
import { ROOM_TYPES } from './data/rooms.js';
import { TRAINING_DEFS } from './data/trainings.js';
import { PROP_DEFS, propCostForLevel } from './data/props.js';
import { FACILITY_DEFS, facilityCostForLevel } from './data/facilities.js';
import { ACADEMY_TRAINING_DEFS, academyTrainingCost, academyTrainingDuration } from './data/academyTrainings.js';
// lobbyRooms value imports removed (unified into lobbyFacilities)
import { LOBBY_FACILITY_DEFS, lobbyFacilityUpgradeCost } from './data/lobbyFacilities.js';
import type { LobbyFacilityType } from './data/lobbyFacilities.js';
import { LOBBY_PROP_DEFS, lobbyPropCostForLevel } from './data/lobbyProps.js';
import { FACILITY_PROP_DEFS, facilityPropCostForLevel } from './data/facilityProps.js';
import type { AcademySkills, GhostRole } from './state.js';
import { CONFIG } from './config.js';
import { getEventById } from './data/events.js';
import { createStreetVisitor } from './systems.js';

// ── Command Types ──────────────────────────────────────────

export type GameCommandType =
  | 'BUY_FLOOR'
  | 'BUILD_ROOM'
  | 'UPGRADE_ROOM'
  | 'UNLOCK_ROOM_TYPE'
  | 'HIRE_GHOST'
  | 'ASSIGN_GHOST'
  | 'UNASSIGN_GHOST'
  | 'LEVEL_UP_GHOST'
  | 'RECOVER_FATIGUE'

  | 'TRAIN_GHOST'
  | 'COMPLETE_TRAINING'
  | 'SKIP_TRAINING'
  | 'BUY_PROP'
  | 'BUILD_FACILITY'
  | 'UPGRADE_FACILITY'
  | 'WATCH_AD_SP_BOOST'
  | 'WATCH_AD_RECOVER_FATIGUE'
  | 'WATCH_AD_VIP_VISITOR'
  | 'ADD_REVIEW'
  | 'TOGGLE_OPEN'
  | 'SET_TICKET_PRICE'
  | 'END_DAY'
  | 'SWITCH_VIEW'
  | 'SET_GHOST_ROLE'
  | 'ASSIGN_LOBBY_STAFF'
  | 'UNASSIGN_LOBBY_STAFF'
  | 'ASSIGN_FACILITY_STAFF'
  | 'UNASSIGN_FACILITY_STAFF'
  | 'START_ACADEMY_TRAINING'
  | 'COMPLETE_ACADEMY_TRAINING'
  | 'SKIP_ACADEMY_TRAINING'
  | 'BUILD_LOBBY_FLOOR'
  | 'BUILD_LOBBY_FLOOR_FACILITY'
  | 'UPGRADE_LOBBY_FLOOR'
  | 'ASSIGN_LOBBY_GHOST'
  | 'UNASSIGN_LOBBY_GHOST'
  | 'BUY_LOBBY_PROP'
  | 'ADD_LOBBY_FLOOR'
  | 'BUILD_FACILITY_FLOOR'
  | 'UPGRADE_FACILITY_FLOOR'
  | 'ASSIGN_FACILITY_GHOST'
  | 'UNASSIGN_FACILITY_GHOST'
  | 'BUY_FACILITY_PROP'
  | 'BUILD_FACILITY_ON_PLOT'
  | 'UPGRADE_FACILITY_PLOT'
  | 'ASSIGN_FACILITY_PLOT_STAFF'
  | 'UNASSIGN_FACILITY_PLOT_STAFF'
  | 'UNLOCK_FACILITY_PLOT'
  | 'SET_FACILITY_PRICE'
  | 'DEMOLISH_FACILITY'
  | 'MOVE_FACILITY'
  | 'TOGGLE_FACILITY_OPEN'
  | 'REQUEST_REPAIR'
  | 'REQUEST_CLEANING'
  | 'UPGRADE_CONDITION'
  | 'UPGRADE_HYGIENE'
  | 'DEMOLISH_ROOM'
  | 'GENERATE_DAILY_MILESTONES'
  | 'COLLECT_MILESTONE_REWARDS'
  | 'START_EVENING'
  | 'SET_GAME_SPEED'
  | 'ADVANCE_TO_NIGHT'
  | 'ADVANCE_TO_DAY'
  | 'HIRE_GHOST_STAFF'
  | 'FIRE_GHOST_STAFF'
  | 'ASSIGN_GHOST_STAFF'
  | 'BUILD_LOBBY_FACILITY'       // legacy, kept for compat
  | 'UPGRADE_LOBBY_FACILITY'     // legacy, kept for compat
  | 'ASSIGN_LOBBY_FACILITY_NPC'  // legacy, kept for compat
  | 'UNASSIGN_LOBBY_FACILITY_NPC' // legacy, kept for compat
  | 'ASSIGN_LOBBY_FLOOR_NPC'
  | 'UNASSIGN_LOBBY_FLOOR_NPC'
  | 'ASSIGN_LOBBY_NPC_GHOST'
  | 'UNASSIGN_LOBBY_NPC_GHOST'
  | 'RESOLVE_EVENT'
  | 'DISMISS_DEFENDED_EVENT'
  | 'SELECT_HAUNTED_BUILDING'
  | 'UNLOCK_HAUNTED_BUILDING'
  | 'SET_HAUNTED_BUILDING_NAME'
  | 'SET_HAUNTED_BUILDING_THEME'
  | 'SET_WORLD_SCREEN'
  | 'ADVANCE_TUTORIAL';

// ── Command Interfaces ─────────────────────────────────────

export interface BuyFloorCommand extends ICommand<'BUY_FLOOR', Record<string, never>> {}
export interface BuildRoomCommand extends ICommand<'BUILD_ROOM', { floorLevel: number; roomType: RoomTypeId }> {}
export interface UpgradeRoomCommand extends ICommand<'UPGRADE_ROOM', { roomId: string }> {}
export interface UnlockRoomTypeCommand extends ICommand<'UNLOCK_ROOM_TYPE', { roomTypeId: RoomTypeId }> {}
export interface HireGhostCommand extends ICommand<'HIRE_GHOST', { ghostCharacterId: GhostId }> {}
export interface AssignGhostCommand extends ICommand<'ASSIGN_GHOST', { ghostInstanceId: string; roomId: string }> {}
export interface UnassignGhostCommand extends ICommand<'UNASSIGN_GHOST', { ghostInstanceId: string }> {}
export interface LevelUpGhostCommand extends ICommand<'LEVEL_UP_GHOST', { ghostInstanceId: string }> {}
export interface RecoverFatigueCommand extends ICommand<'RECOVER_FATIGUE', { ghostInstanceId: string }> {}

export interface TrainGhostCommand extends ICommand<'TRAIN_GHOST', { ghostInstanceId: string; trainingId: TrainingId }> {}
export interface CompleteTrainingCommand extends ICommand<'COMPLETE_TRAINING', { ghostInstanceId: string }> {}
export interface SkipTrainingCommand extends ICommand<'SKIP_TRAINING', { ghostInstanceId: string }> {}
export interface BuyPropCommand extends ICommand<'BUY_PROP', { roomId: string; propId: PropId }> {}
export interface BuildFacilityCommand extends ICommand<'BUILD_FACILITY', { facilityId: FacilityId }> {}
export interface UpgradeFacilityCommand extends ICommand<'UPGRADE_FACILITY', { facilityId: FacilityId }> {}
export interface WatchAdSpBoostCommand extends ICommand<'WATCH_AD_SP_BOOST', Record<string, never>> {}
export interface WatchAdRecoverFatigueCommand extends ICommand<'WATCH_AD_RECOVER_FATIGUE', { ghostInstanceId: string }> {}
export interface WatchAdVipVisitorCommand extends ICommand<'WATCH_AD_VIP_VISITOR', Record<string, never>> {}
export interface AddReviewCommand extends ICommand<'ADD_REVIEW', { stars: 1 | 2 | 3 | 4 | 5; textKey: string; ghostNameKey: string | null; visitorType?: string; floorLevel?: number; satisfaction?: number }> {}
export interface ToggleOpenCommand extends ICommand<'TOGGLE_OPEN', Record<string, never>> {}
export interface SetTicketPriceCommand extends ICommand<'SET_TICKET_PRICE', { price: number }> {}
export interface EndDayCommand extends ICommand<'END_DAY', Record<string, never>> {}
export interface SwitchViewCommand extends ICommand<'SWITCH_VIEW', { view: MainView }> {}
export interface SetGhostRoleCommand extends ICommand<'SET_GHOST_ROLE', { ghostInstanceId: string; role: GhostRole }> {}
export interface AssignLobbyStaffCommand extends ICommand<'ASSIGN_LOBBY_STAFF', { ghostInstanceId: string }> {}
export interface UnassignLobbyStaffCommand extends ICommand<'UNASSIGN_LOBBY_STAFF', { ghostInstanceId: string }> {}
export interface AssignFacilityStaffCommand extends ICommand<'ASSIGN_FACILITY_STAFF', { ghostInstanceId: string; facilityId: FacilityId }> {}
export interface UnassignFacilityStaffCommand extends ICommand<'UNASSIGN_FACILITY_STAFF', { ghostInstanceId: string }> {}
export interface StartAcademyTrainingCommand extends ICommand<'START_ACADEMY_TRAINING', { ghostInstanceId: string; trainingId: AcademyTrainingId }> {}
export interface CompleteAcademyTrainingCommand extends ICommand<'COMPLETE_ACADEMY_TRAINING', { ghostInstanceId: string }> {}
export interface SkipAcademyTrainingCommand extends ICommand<'SKIP_ACADEMY_TRAINING', { ghostInstanceId: string }> {}

// Lobby Building commands (unified: floors + facilities merged)
export interface BuildLobbyFloorCommand extends ICommand<'BUILD_LOBBY_FLOOR', { roomType: LobbyRoomType }> {} // legacy
export interface BuildLobbyFloorFacilityCommand extends ICommand<'BUILD_LOBBY_FLOOR_FACILITY', { level: number; facilityType: LobbyFacilityType }> {}
export interface UpgradeLobbyFloorCommand extends ICommand<'UPGRADE_LOBBY_FLOOR', { level: number }> {}
export interface AssignLobbyGhostCommand extends ICommand<'ASSIGN_LOBBY_GHOST', { ghostInstanceId: string; floorLevel: number }> {}
export interface UnassignLobbyGhostCommand extends ICommand<'UNASSIGN_LOBBY_GHOST', { floorLevel: number }> {}
export interface BuyLobbyPropCommand extends ICommand<'BUY_LOBBY_PROP', { floorLevel: number; propId: LobbyPropId }> {}
export interface AddLobbyFloorCommand extends ICommand<'ADD_LOBBY_FLOOR', Record<string, never>> {}
export interface AssignLobbyFloorNpcCommand extends ICommand<'ASSIGN_LOBBY_FLOOR_NPC', { level: number; ghostInstanceId: string }> {}
export interface UnassignLobbyFloorNpcCommand extends ICommand<'UNASSIGN_LOBBY_FLOOR_NPC', { level: number }> {}
export interface DemolishLobbyFacilityCommand extends ICommand<'DEMOLISH_LOBBY_FACILITY_FLOOR', { level: number }> {}

// Facility Building commands
export interface BuildFacilityFloorCommand extends ICommand<'BUILD_FACILITY_FLOOR', { facilityId: FacilityId }> {}
export interface UpgradeFacilityFloorCommand extends ICommand<'UPGRADE_FACILITY_FLOOR', { level: number }> {}
export interface AssignFacilityGhostCommand extends ICommand<'ASSIGN_FACILITY_GHOST', { ghostInstanceId: string; floorLevel: number }> {}
export interface UnassignFacilityGhostCommand extends ICommand<'UNASSIGN_FACILITY_GHOST', { floorLevel: number }> {}
export interface BuyFacilityPropCommand extends ICommand<'BUY_FACILITY_PROP', { floorLevel: number; propId: FacilityPropId }> {}

// Facility Map (Plot-based) commands
export interface BuildFacilityOnPlotCommand extends ICommand<'BUILD_FACILITY_ON_PLOT', { plotId: number; facilityType: FacilityId }> {}
export interface UpgradeFacilityPlotCommand extends ICommand<'UPGRADE_FACILITY_PLOT', { plotId: number }> {}
export interface AssignFacilityPlotStaffCommand extends ICommand<'ASSIGN_FACILITY_PLOT_STAFF', { plotId: number; ghostInstanceId: string }> {}
export interface UnassignFacilityPlotStaffCommand extends ICommand<'UNASSIGN_FACILITY_PLOT_STAFF', { plotId: number }> {}
export interface UnlockFacilityPlotCommand extends ICommand<'UNLOCK_FACILITY_PLOT', { plotId: number }> {}
export interface SetFacilityPriceCommand extends ICommand<'SET_FACILITY_PRICE', { plotId: number; price: number }> {}
export interface DemolishFacilityCommand extends ICommand<'DEMOLISH_FACILITY', { plotId: number }> {}
export interface MoveFacilityCommand extends ICommand<'MOVE_FACILITY', { fromPlotId: number; toPlotId: number }> {}
export interface ToggleFacilityOpenCommand extends ICommand<'TOGGLE_FACILITY_OPEN', { plotId: number }> {}

// Room condition/hygiene commands
export interface RequestRepairCommand extends ICommand<'REQUEST_REPAIR', { floorLevel: number }> {}
export interface RequestCleaningCommand extends ICommand<'REQUEST_CLEANING', { floorLevel: number }> {}
export interface UpgradeConditionCommand extends ICommand<'UPGRADE_CONDITION', { floorLevel: number }> {}
export interface UpgradeHygieneCommand extends ICommand<'UPGRADE_HYGIENE', { floorLevel: number }> {}
export interface DemolishRoomCommand extends ICommand<'DEMOLISH_ROOM', { floorLevel: number }> {}

// Daily Milestones commands
export interface GenerateDailyMilestonesCommand extends ICommand<'GENERATE_DAILY_MILESTONES', Record<string, never>> {}
export interface CollectMilestoneRewardsCommand extends ICommand<'COLLECT_MILESTONE_REWARDS', Record<string, never>> {}

// Day Phase commands
export interface StartEveningCommand extends ICommand<'START_EVENING', Record<string, never>> {}
export interface SetGameSpeedCommand extends ICommand<'SET_GAME_SPEED', { speed: 1 | 2 }> {}
export interface AdvanceToNightCommand extends ICommand<'ADVANCE_TO_NIGHT', Record<string, never>> {}
export interface AdvanceToDayCommand extends ICommand<'ADVANCE_TO_DAY', Record<string, never>> {}

// Ghost Staff commands
export interface HireGhostStaffCommand extends ICommand<'HIRE_GHOST_STAFF', { staffType: GhostStaffType }> {}
export interface FireGhostStaffCommand extends ICommand<'FIRE_GHOST_STAFF', { staffId: string }> {}
export interface AssignGhostStaffCommand extends ICommand<'ASSIGN_GHOST_STAFF', { staffId: string; target: string }> {}

// Lobby Facility commands
export interface BuildLobbyFacilityCommand extends ICommand<'BUILD_LOBBY_FACILITY', { facilityType: LobbyFacilityType }> {}
export interface UpgradeLobbyFacilityCommand extends ICommand<'UPGRADE_LOBBY_FACILITY', { facilityId: string }> {}
export interface AssignLobbyFacilityNpcCommand extends ICommand<'ASSIGN_LOBBY_FACILITY_NPC', { facilityId: string; ghostInstanceId: string }> {}
export interface UnassignLobbyFacilityNpcCommand extends ICommand<'UNASSIGN_LOBBY_FACILITY_NPC', { facilityId: string }> {}

// Lobby NPC Ghost commands
export interface AssignLobbyNpcGhostCommand extends ICommand<'ASSIGN_LOBBY_NPC_GHOST', { ghostId: string }> {}
export interface UnassignLobbyNpcGhostCommand extends ICommand<'UNASSIGN_LOBBY_NPC_GHOST', { ghostId: string }> {}

// Random Event commands
export interface ResolveEventCommand extends ICommand<'RESOLVE_EVENT', { eventId: string; choiceIndex: number }> {}
export interface DismissDefendedEventCommand extends ICommand<'DISMISS_DEFENDED_EVENT', { eventId: string }> {}
export interface SelectHauntedBuildingCommand extends ICommand<'SELECT_HAUNTED_BUILDING', { buildingId: string }> {}
export interface UnlockHauntedBuildingCommand extends ICommand<'UNLOCK_HAUNTED_BUILDING', { buildingId: string }> {}
export interface SetHauntedBuildingNameCommand extends ICommand<'SET_HAUNTED_BUILDING_NAME', { buildingId: string; name: string }> {}
export interface SetHauntedBuildingThemeCommand extends ICommand<'SET_HAUNTED_BUILDING_THEME', { buildingId: string; themeId: HauntedThemeId }> {}
export interface SetWorldScreenCommand extends ICommand<'SET_WORLD_SCREEN', { screen: WorldScreen }> {}
export interface AdvanceTutorialCommand extends ICommand<'ADVANCE_TUTORIAL', { step: number }> {}

export type GameCommand =
  | BuyFloorCommand
  | BuildRoomCommand
  | UpgradeRoomCommand
  | UnlockRoomTypeCommand
  | HireGhostCommand
  | AssignGhostCommand
  | UnassignGhostCommand
  | LevelUpGhostCommand
  | RecoverFatigueCommand

  | TrainGhostCommand
  | CompleteTrainingCommand
  | SkipTrainingCommand
  | BuyPropCommand
  | BuildFacilityCommand
  | UpgradeFacilityCommand
  | WatchAdSpBoostCommand
  | WatchAdRecoverFatigueCommand
  | WatchAdVipVisitorCommand
  | AddReviewCommand
  | ToggleOpenCommand
  | SetTicketPriceCommand
  | EndDayCommand
  | SwitchViewCommand
  | SetGhostRoleCommand
  | AssignLobbyStaffCommand
  | UnassignLobbyStaffCommand
  | AssignFacilityStaffCommand
  | UnassignFacilityStaffCommand
  | StartAcademyTrainingCommand
  | CompleteAcademyTrainingCommand
  | SkipAcademyTrainingCommand
  | BuildLobbyFloorCommand
  | BuildLobbyFloorFacilityCommand
  | UpgradeLobbyFloorCommand
  | AssignLobbyGhostCommand
  | UnassignLobbyGhostCommand
  | BuyLobbyPropCommand
  | AddLobbyFloorCommand
  | AssignLobbyFloorNpcCommand
  | UnassignLobbyFloorNpcCommand
  | DemolishLobbyFacilityCommand
  | BuildFacilityFloorCommand
  | UpgradeFacilityFloorCommand
  | AssignFacilityGhostCommand
  | UnassignFacilityGhostCommand
  | BuyFacilityPropCommand
  | BuildFacilityOnPlotCommand
  | UpgradeFacilityPlotCommand
  | AssignFacilityPlotStaffCommand
  | UnassignFacilityPlotStaffCommand
  | UnlockFacilityPlotCommand
  | SetFacilityPriceCommand
  | DemolishFacilityCommand
  | MoveFacilityCommand
  | ToggleFacilityOpenCommand
  | RequestRepairCommand
  | RequestCleaningCommand
  | UpgradeConditionCommand
  | UpgradeHygieneCommand
  | DemolishRoomCommand
  | GenerateDailyMilestonesCommand
  | CollectMilestoneRewardsCommand
  | StartEveningCommand
  | SetGameSpeedCommand
  | AdvanceToNightCommand
  | AdvanceToDayCommand
  | HireGhostStaffCommand
  | FireGhostStaffCommand
  | AssignGhostStaffCommand
  | BuildLobbyFacilityCommand
  | UpgradeLobbyFacilityCommand
  | AssignLobbyFacilityNpcCommand
  | UnassignLobbyFacilityNpcCommand
  | AssignLobbyNpcGhostCommand
  | UnassignLobbyNpcGhostCommand
  | ResolveEventCommand
  | DismissDefendedEventCommand
  | SelectHauntedBuildingCommand
  | UnlockHauntedBuildingCommand
  | SetHauntedBuildingNameCommand
  | SetHauntedBuildingThemeCommand
  | SetWorldScreenCommand
  | AdvanceTutorialCommand;

// ── Helpers ────────────────────────────────────────────────

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function floorCostFor(nextFloorCount: number): number {
  const costs = CONFIG.floorCosts;
  if (nextFloorCount < costs.length) {
    return costs[nextFloorCount] ?? 0;
  }
  return costs[costs.length - 1] * Math.pow(5, nextFloorCount - costs.length + 1);
}

function roomUpgradeCostFor(floorLevel: number, toLevel: 2 | 3): number {
  const base = floorCostFor(floorLevel);
  const fraction = toLevel === 2 ? CONFIG.roomUpgrade2Fraction : CONFIG.roomUpgrade3Fraction;
  return Math.max(50, Math.floor(base * fraction));
}

function findRoom(state: GameStateData, roomId: string): Room | null {
  for (const floor of state.building) {
    if (floor.room && floor.room.id === roomId) return floor.room;
  }
  return null;
}

function mapRoom(
  state: GameStateData,
  roomId: string,
  updater: (r: Room) => Room,
): GameStateData {
  return {
    ...state,
    building: state.building.map((floor) => ({
      ...floor,
      room: floor.room && floor.room.id === roomId ? updater(floor.room) : floor.room,
    })),
  };
}

function mapGhost(
  state: GameStateData,
  ghostId: string,
  updater: (g: GhostInstance) => GhostInstance,
): GameStateData {
  return {
    ...state,
    ghosts: state.ghosts.map((g) => (g.id === ghostId ? updater(g) : g)),
  };
}

// ── Executor ───────────────────────────────────────────────

export const executor = new CommandExecutor<GameStateData, GameCommand>();

// BUY_FLOOR
executor.register('BUY_FLOOR', (state, _cmd: BuyFloorCommand) => {
  const nextCount = state.building.length + 1;
  if (nextCount > getMaxFloorsForSelectedBuilding(state)) return state;
  const cost = floorCostFor(nextCount);
  if (state.sp < cost) return state;

  const newFloor: Floor = {
    level: nextCount,
    room: null,
  };
  return {
    ...state,
    sp: state.sp - cost,
    building: [...state.building, newFloor],
  };
});

// BUILD_ROOM
executor.register('BUILD_ROOM', (state, cmd: BuildRoomCommand) => {
  const { floorLevel, roomType } = cmd.payload;

  if (!state.unlockedRoomTypes.includes(roomType)) return state;

  const floor = state.building.find((f) => f.level === floorLevel);
  if (!floor) return state;
  if (floor.room !== null) return state;

  const newRoom: Room = {
    id: generateId(),
    type: roomType,
    upgradeLevel: 1,
    assignedGhostId: null,
    props: [],
    condition: 100,
    hygiene: 100,
    conditionUpgrade: 0,
    hygieneUpgrade: 0,
    collapsed: false,
    underRepair: false,
    underCleaning: false,
    repairEndsAt: 0,
    cleaningEndsAt: 0,
  };

  return {
    ...state,
    building: state.building.map((f) =>
      f.level === floorLevel
        ? { ...f, room: newRoom }
        : f,
    ),
  };
});

// UPGRADE_ROOM
executor.register('UPGRADE_ROOM', (state, cmd: UpgradeRoomCommand) => {
  const { roomId } = cmd.payload;
  const room = findRoom(state, roomId);
  if (!room) return state;
  if (room.upgradeLevel >= 3) return state;

  let floorLevel = 1;
  for (const floor of state.building) {
    if (floor.room && floor.room.id === roomId) {
      floorLevel = floor.level;
      break;
    }
  }

  const toLevel = (room.upgradeLevel + 1) as 2 | 3;
  const cost = roomUpgradeCostFor(floorLevel, toLevel);
  if (state.sp < cost) return state;

  return {
    ...mapRoom(state, roomId, (r) => ({ ...r, upgradeLevel: toLevel })),
    sp: state.sp - cost,
  };
});

// UNLOCK_ROOM_TYPE
executor.register('UNLOCK_ROOM_TYPE', (state, cmd: UnlockRoomTypeCommand) => {
  const { roomTypeId } = cmd.payload;
  if (state.unlockedRoomTypes.includes(roomTypeId)) return state;

  const def = ROOM_TYPES[roomTypeId];
  if (!def) return state;
  if (state.sp < def.unlockCost) return state;

  // Check unlock conditions
  if (def.unlockCondition) {
    const { minDay, minFame, requiredRoom } = def.unlockCondition;
    if (minDay !== undefined && state.dayNumber < minDay) return state;
    if (minFame !== undefined && state.fame < minFame) return state;
    if (requiredRoom !== undefined && !state.unlockedRoomTypes.includes(requiredRoom)) return state;
  }

  return {
    ...state,
    sp: state.sp - def.unlockCost,
    unlockedRoomTypes: [...state.unlockedRoomTypes, roomTypeId],
  };
});

// HIRE_GHOST
executor.register('HIRE_GHOST', (state, cmd: HireGhostCommand) => {
  const { ghostCharacterId } = cmd.payload;
  const def = GHOST_DEFS[ghostCharacterId];
  if (!def) return state;
  if (state.sp < def.hireCost) return state;

  // Build academy skills - pro ghosts start at level 3
  const startLevel = def.startingSkillLevel ?? 0;
  const academySkills: AcademySkills = {
    acting: startLevel,
    human_relations: startLevel,
    psychology: startLevel,
    special_fx: startLevel,
    fitness: startLevel,
    teamwork: startLevel,
    customer_service: startLevel,
  };

  const newGhost: GhostInstance = {
    id: generateId(),
    characterId: ghostCharacterId,
    level: 1,
    experience: 0,
    fatigue: 0,
    assignedRoomId: null,
    training: null,
    trainingBonus: 0,
    trainingCount: 0,
    role: 'scare',
    assignedFacilityId: null,
    academySession: null,
    academySkills,
    hiredAt: state.dayNumber,
    praiseCount: 0,
  };

  return {
    ...state,
    sp: state.sp - def.hireCost,
    ghosts: [...state.ghosts, newGhost],
  };
});

// ASSIGN_GHOST
executor.register('ASSIGN_GHOST', (state, cmd: AssignGhostCommand) => {
  const { ghostInstanceId, roomId } = cmd.payload;
  const room = findRoom(state, roomId);
  if (!room) return state;

  let newState = state;

  if (room.assignedGhostId) {
    newState = mapGhost(newState, room.assignedGhostId, (g) => ({
      ...g,
      assignedRoomId: null,
    }));
  }

  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  // Can't assign a ghost that's training or in academy
  if (ghost.training) return state;
  if (ghost.academySession) return state;
  if (ghost.assignedFacilityId) return state;
  if (state.lobbyStaff.includes(ghostInstanceId)) return state;

  if (ghost.assignedRoomId) {
    newState = mapRoom(newState, ghost.assignedRoomId, (r) => ({
      ...r,
      assignedGhostId: null,
    }));
  }

  newState = mapGhost(newState, ghostInstanceId, (g) => ({
    ...g,
    assignedRoomId: roomId,
  }));
  newState = mapRoom(newState, roomId, (r) => ({
    ...r,
    assignedGhostId: ghostInstanceId,
  }));

  return newState;
});

// UNASSIGN_GHOST
executor.register('UNASSIGN_GHOST', (state, cmd: UnassignGhostCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;

  let newState = state;
  if (ghost.assignedRoomId) {
    newState = mapRoom(newState, ghost.assignedRoomId, (r) => ({
      ...r,
      assignedGhostId: null,
    }));
  }
  newState = mapGhost(newState, ghostInstanceId, (g) => ({
    ...g,
    assignedRoomId: null,
  }));
  return newState;
});

// LEVEL_UP_GHOST
executor.register('LEVEL_UP_GHOST', (state, cmd: LevelUpGhostCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  if (ghost.level >= CONFIG.ghostMaxLevel) return state;

  const expRequired = Math.floor(
    CONFIG.ghostLevelUpExpBase * Math.pow(CONFIG.ghostLevelUpExpGrowth, ghost.level - 1),
  );
  if (ghost.experience < expRequired) return state;

  return mapGhost(state, ghostInstanceId, (g) => ({
    ...g,
    level: g.level + 1,
    experience: g.experience - expRequired,
  }));
});

// RECOVER_FATIGUE
executor.register('RECOVER_FATIGUE', (state, cmd: RecoverFatigueCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  if (state.sp < CONFIG.fatigueRecoveryCostSp) return state;

  return {
    ...mapGhost(state, ghostInstanceId, (g) => ({ ...g, fatigue: 0 })),
    sp: state.sp - CONFIG.fatigueRecoveryCostSp,
  };
});

// COLLECT_OFFLINE_SP (removed)

// ── New Commands ───────────────────────────────────────────

// TRAIN_GHOST
executor.register('TRAIN_GHOST', (state, cmd: TrainGhostCommand) => {
  const { ghostInstanceId, trainingId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;

  // Can't train if already training
  if (ghost.training) return state;
  // Can't train if assigned to a room
  if (ghost.assignedRoomId) return state;

  const trainingDef = TRAINING_DEFS[trainingId];
  if (!trainingDef) return state;
  if (state.sp < trainingDef.costSp) return state;

  const now = Date.now();

  return {
    ...mapGhost(state, ghostInstanceId, (g) => ({
      ...g,
      training: {
        trainingId,
        startedAt: now,
        completesAt: now + trainingDef.durationMs,
      },
    })),
    sp: state.sp - trainingDef.costSp,
  };
});

// COMPLETE_TRAINING
executor.register('COMPLETE_TRAINING', (state, cmd: CompleteTrainingCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  if (!ghost.training) return state;

  const now = Date.now();
  if (now < ghost.training.completesAt) return state;

  const trainingDef = TRAINING_DEFS[ghost.training.trainingId];
  if (!trainingDef) return state;

  return mapGhost(state, ghostInstanceId, (g) => ({
    ...g,
    training: null,
    trainingBonus: g.trainingBonus + trainingDef.scareBonus,
    trainingCount: g.trainingCount + 1,
    experience: g.experience + trainingDef.expReward,
    fatigue: Math.max(0, g.fatigue - trainingDef.fatigueReduction),
  }));
});

// SKIP_TRAINING (via ad)
executor.register('SKIP_TRAINING', (state, cmd: SkipTrainingCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  if (!ghost.training) return state;

  const trainingDef = TRAINING_DEFS[ghost.training.trainingId];
  if (!trainingDef) return state;

  return mapGhost(state, ghostInstanceId, (g) => ({
    ...g,
    training: null,
    trainingBonus: g.trainingBonus + trainingDef.scareBonus,
    trainingCount: g.trainingCount + 1,
    experience: g.experience + trainingDef.expReward,
    fatigue: Math.max(0, g.fatigue - trainingDef.fatigueReduction),
  }));
});

// BUY_PROP
executor.register('BUY_PROP', (state, cmd: BuyPropCommand) => {
  const { roomId, propId } = cmd.payload;
  const room = findRoom(state, roomId);
  if (!room) return state;

  const propDef = PROP_DEFS[propId];
  if (!propDef) return state;

  const existing = room.props.find((p) => p.propId === propId);
  const currentLevel = existing ? existing.level : 0;
  const nextLevel = currentLevel + 1;
  if (nextLevel > propDef.maxLevel) return state;

  const cost = propCostForLevel(propId, nextLevel);
  if (state.sp < cost) return state;

  const newProps: RoomProp[] = existing
    ? room.props.map((p) => p.propId === propId ? { ...p, level: nextLevel } : p)
    : [...room.props, { propId, level: 1 }];

  return {
    ...mapRoom(state, roomId, (r) => ({ ...r, props: newProps })),
    sp: state.sp - cost,
  };
});

// BUILD_FACILITY
executor.register('BUILD_FACILITY', (state, cmd: BuildFacilityCommand) => {
  const { facilityId } = cmd.payload;
  const facilityDef = FACILITY_DEFS[facilityId];
  if (!facilityDef) return state;

  // Check if already built
  if (state.facilities.some((f) => f.facilityId === facilityId)) return state;

  if (state.sp < facilityDef.buildCost) return state;

  const newFacility: FacilityInstance = {
    facilityId,
    level: 1,
    assignedStaffId: null,
  };

  return {
    ...state,
    sp: state.sp - facilityDef.buildCost,
    facilities: [...state.facilities, newFacility],
  };
});

// UPGRADE_FACILITY
executor.register('UPGRADE_FACILITY', (state, cmd: UpgradeFacilityCommand) => {
  const { facilityId } = cmd.payload;
  const facility = state.facilities.find((f) => f.facilityId === facilityId);
  if (!facility) return state;

  const facilityDef = FACILITY_DEFS[facilityId];
  if (!facilityDef) return state;
  if (facility.level >= facilityDef.maxLevel) return state;

  const nextLevel = facility.level + 1;
  const cost = facilityCostForLevel(facilityId, nextLevel);
  if (state.sp < cost) return state;

  return {
    ...state,
    sp: state.sp - cost,
    facilities: state.facilities.map((f) =>
      f.facilityId === facilityId ? { ...f, level: nextLevel } : f,
    ),
  };
});

// WATCH_AD_SP_BOOST - 2x SP for 30 seconds
executor.register('WATCH_AD_SP_BOOST', (state, _cmd: WatchAdSpBoostCommand) => {
  const now = Date.now();
  const boost: AdBoost = {
    type: 'sp_double',
    expiresAt: now + 30000,
  };
  return {
    ...state,
    adBoosts: [...state.adBoosts.filter((b) => b.expiresAt > now), boost],
  };
});

// WATCH_AD_RECOVER_FATIGUE - free fatigue recovery
executor.register('WATCH_AD_RECOVER_FATIGUE', (state, cmd: WatchAdRecoverFatigueCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;

  return mapGhost(state, ghostInstanceId, (g) => ({ ...g, fatigue: 0 }));
});

// WATCH_AD_VIP_VISITOR - summon VIP visitor via street system
executor.register('WATCH_AD_VIP_VISITOR', (state, _cmd: WatchAdVipVisitorCommand) => {
  // Flag the system to spawn a VIP on next tick + reset lobby spawn
  return {
    ...state,
    nextVisitorSpawnAt: 0,
    pendingVipSpawn: true,
  };
});

// ADD_REVIEW
executor.register('ADD_REVIEW', (state, cmd: AddReviewCommand) => {
  const { stars, textKey, ghostNameKey, visitorType, floorLevel, satisfaction } = cmd.payload;
  const review: import('./state.js').ReviewEntry = {
    id: generateId(),
    stars,
    textKey,
    ghostNameKey,
    timestamp: Date.now(),
    visitorType,
    floorLevel,
    satisfaction,
  };
  // Keep max 20 reviews
  const newReviews = [review, ...state.reviews].slice(0, 20);
  return {
    ...state,
    reviews: newReviews,
  };
});

// TOGGLE_OPEN - open or close for business
executor.register('TOGGLE_OPEN', (state, _cmd: ToggleOpenCommand) => {
  const wasOpen = state.isOpen;
  const now = Date.now();

  const newState = {
    ...state,
    isOpen: !wasOpen,
    dayStartTime: wasOpen ? state.dayStartTime : now,
    // Sync day phase: opening = evening, closing = day
    dayPhase: (wasOpen ? 'day' : 'evening') as DayPhase,
    dayPhaseStartedAt: now,
  };

  // Generate daily milestones when opening for business
  if (!wasOpen && newState.dailyMilestones.length === 0) {
    const milestonePool: Array<{ nameKey: string; target: number; reward: number }> = [
      { nameKey: 'milestone_visitors_5', target: 5, reward: 200 },
      { nameKey: 'milestone_visitors_10', target: 10, reward: 500 },
      { nameKey: 'milestone_visitors_20', target: 20, reward: 1500 },
      { nameKey: 'milestone_earnings_1000', target: 1000, reward: 300 },
      { nameKey: 'milestone_earnings_5000', target: 5000, reward: 800 },
      { nameKey: 'milestone_earnings_10000', target: 10000, reward: 2000 },
      { nameKey: 'milestone_flee_0', target: 0, reward: 500 },
      { nameKey: 'milestone_flee_1', target: 1, reward: 300 },
      { nameKey: 'milestone_rooms_3', target: 3, reward: 400 },
      { nameKey: 'milestone_rooms_5', target: 5, reward: 800 },
      { nameKey: 'milestone_ghosts_2', target: 2, reward: 300 },
      { nameKey: 'milestone_ghosts_3', target: 3, reward: 600 },
    ];
    const shuffled = [...milestonePool].sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, 5);
    newState.dailyMilestones = picked.map((m, i) => ({
      id: `milestone-${now}-${i}`,
      nameKey: m.nameKey,
      target: m.target,
      current: 0,
      completed: false,
      reward: m.reward,
      collected: false,
    }));
  }

  return newState;
});

// SET_TICKET_PRICE - change admission fee (min 1, max 100)
executor.register('SET_TICKET_PRICE', (state, cmd: SetTicketPriceCommand) => {
  const { price } = cmd.payload;
  const clamped = Math.max(1, Math.min(100, Math.round(price)));
  return {
    ...state,
    ticketPrice: clamped,
  };
});

// END_DAY - finish today's business, advance to next day
executor.register('END_DAY', (state, _cmd: EndDayCommand) => {
  // Deduct salaries
  let totalSalaries = 0;
  for (const staff of (state.ghostStaff || [])) {
    totalSalaries += staff.salary;
  }
  const newSp = Math.max(0, state.sp - totalSalaries);

  // Apply daily lobby facility unique effects
  let reputationBonus = 0;
  let revisitBonus = 0;
  for (const floor of (state.lobbyBuilding || [])) {
    if (!floor.facilityType) continue;
    const def = LOBBY_FACILITY_DEFS[floor.facilityType];
    if (!def) continue;
    const lvl = Math.max(1, floor.facilityLevel);
    if (def.uniqueEffect.type === 'reputation_per_day') {
      reputationBonus += def.uniqueEffect.value * lvl;
    }
    if (def.uniqueEffect.type === 'revisit_rate_up') {
      revisitBonus += def.uniqueEffect.value;
    }
  }

  // Photo zone: daily reputation gain
  const newReputation = Math.min(100, (state.reputation || 50) + reputationBonus);
  // Souvenir booth: recommendation pool boost from revisit rate
  const newRecommendationPool = (state.recommendationPool || 0) + Math.round(revisitBonus * state.todayVisitors);

  return {
    ...state,
    sp: newSp,
    isOpen: false,
    dayNumber: state.dayNumber + 1,
    todayVisitors: 0,
    todayEarnings: 0,
    dayStartTime: 0,
    todayFleeCount: 0,
    timePhase: 'day' as const,
    timePhaseStartAt: 0,
    dailyMilestones: [],
    dayPhase: 'day' as DayPhase,
    dayPhaseStartedAt: Date.now(),
    gameSpeed: 1 as const,
    todaySalaries: totalSalaries,
    visitorGroup: null,
    reputation: newReputation,
    recommendationPool: newRecommendationPool,
  };
});

// ── Daily Milestones ───────────────────────────────────────

// GENERATE_DAILY_MILESTONES - create 5 random milestones for the day
executor.register('GENERATE_DAILY_MILESTONES', (state, _cmd: GenerateDailyMilestonesCommand) => {
  // Don't regenerate if already exist
  if (state.dailyMilestones.length > 0) return state;

  const milestonePool: Array<{ nameKey: string; target: number; reward: number }> = [
    { nameKey: 'milestone_visitors_5', target: 5, reward: 200 },
    { nameKey: 'milestone_visitors_10', target: 10, reward: 500 },
    { nameKey: 'milestone_visitors_20', target: 20, reward: 1500 },
    { nameKey: 'milestone_earnings_1000', target: 1000, reward: 300 },
    { nameKey: 'milestone_earnings_5000', target: 5000, reward: 800 },
    { nameKey: 'milestone_earnings_10000', target: 10000, reward: 2000 },
    { nameKey: 'milestone_flee_0', target: 0, reward: 500 },
    { nameKey: 'milestone_flee_1', target: 1, reward: 300 },
    { nameKey: 'milestone_rooms_3', target: 3, reward: 400 },
    { nameKey: 'milestone_rooms_5', target: 5, reward: 800 },
    { nameKey: 'milestone_ghosts_2', target: 2, reward: 300 },
    { nameKey: 'milestone_ghosts_3', target: 3, reward: 600 },
  ];

  // Shuffle and pick 5
  const shuffled = [...milestonePool].sort(() => Math.random() - 0.5);
  const picked = shuffled.slice(0, 5);

  const milestones: import('./state.js').DailyMilestone[] = picked.map((m, i) => ({
    id: `milestone-${Date.now()}-${i}`,
    nameKey: m.nameKey,
    target: m.target,
    current: 0,
    completed: false,
    reward: m.reward,
    collected: false,
  }));

  return {
    ...state,
    dailyMilestones: milestones,
  };
});

// COLLECT_MILESTONE_REWARDS - collect all completed milestone rewards
executor.register('COLLECT_MILESTONE_REWARDS', (state, _cmd: CollectMilestoneRewardsCommand) => {
  let totalReward = 0;
  const updated = state.dailyMilestones.map((m) => {
    if (m.completed && !m.collected) {
      totalReward += m.reward;
      return { ...m, collected: true };
    }
    return m;
  });
  return {
    ...state,
    dailyMilestones: updated,
    sp: state.sp + totalReward,
    totalSpEarned: state.totalSpEarned + totalReward,
    todayEarnings: state.todayEarnings + totalReward,
  };
});

// ── View System ────────────────────────────────────────────

executor.register('SET_WORLD_SCREEN', (state, cmd: SetWorldScreenCommand) => {
  const { screen } = cmd.payload;
  return { ...state, worldScreen: screen };
});

executor.register('SELECT_HAUNTED_BUILDING', (state, cmd: SelectHauntedBuildingCommand) => {
  const { buildingId } = cmd.payload;
  return {
    ...applyHauntedBuildingSelection(state, buildingId),
    worldScreen: 'haunted_building',
    currentView: 'building' as MainView,
  };
});

executor.register('UNLOCK_HAUNTED_BUILDING', (state, cmd: UnlockHauntedBuildingCommand) => {
  const { buildingId } = cmd.payload;
  const meta = getHauntedBuildingMeta(state, buildingId);
  if (!meta || meta.isUnlocked || !meta.themeId || !meta.name.trim()) return state;

  const unlockCost = 5000 * Number(meta.id.replace('building_', ''));
  if (state.sp < unlockCost) return state;

  const nextMetas = unlockHauntedBuildingMeta(state, buildingId);
  const nextMeta = nextMetas.find((item) => item.id === buildingId);
  if (!nextMeta) return state;

  return {
    ...state,
    sp: state.sp - unlockCost,
    hauntedBuildingMetas: nextMetas,
    hauntedBuildings: state.hauntedBuildings.some((building) => building.id === buildingId)
      ? state.hauntedBuildings
      : [...state.hauntedBuildings, cloneBuildingStateFromCurrent(state, nextMeta)],
  };
});

executor.register('SET_HAUNTED_BUILDING_NAME', (state, cmd: SetHauntedBuildingNameCommand) => {
  const { buildingId, name } = cmd.payload;
  const trimmed = name.trim();
  if (!trimmed) return state;

  return {
    ...state,
    hauntedBuildingMetas: state.hauntedBuildingMetas.map((meta) => (
      meta.id === buildingId ? { ...meta, name: trimmed } : meta
    )),
    hauntedBuildings: state.hauntedBuildings.map((building) => (
      building.id === buildingId ? { ...building, name: trimmed } : building
    )),
  };
});

executor.register('SET_HAUNTED_BUILDING_THEME', (state, cmd: SetHauntedBuildingThemeCommand) => {
  const { buildingId, themeId } = cmd.payload;
  const nextMetas = configureHauntedBuildingMeta(state, buildingId, getHauntedBuildingMeta(state, buildingId)?.name ?? '', themeId);
  const meta = nextMetas.find((m) => m.id === buildingId);
  if (!meta) return state;

  // If this building is already in hauntedBuildings (unlocked), update it too
  const nextBuildings = state.hauntedBuildings.map((building) =>
    building.id === buildingId
      ? { ...building, themeId, buildingExteriorAsset: meta.buildingExteriorAsset, lobbyStyleId: meta.lobbyStyleId }
      : building,
  );

  const nextState = { ...state, hauntedBuildingMetas: nextMetas, hauntedBuildings: nextBuildings };

  // If this is the currently selected building, also update top-level fields
  if (buildingId === state.selectedHauntedBuildingId) {
    return syncSelectedMetaFromBuilding(mapStateToSelectedBuilding(nextState));
  }
  return nextState;
});


// SWITCH_VIEW
executor.register('SWITCH_VIEW', (state, cmd: SwitchViewCommand) => {
  const { view } = cmd.payload;
  return { ...state, currentView: view };
});

// ── Lobby Staff System ─────────────────────────────────────

// ASSIGN_LOBBY_STAFF
executor.register('ASSIGN_LOBBY_STAFF', (state, cmd: AssignLobbyStaffCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  // Must be idle (no room, no training, no facility, no academy)
  if (ghost.assignedRoomId || ghost.training || ghost.assignedFacilityId || ghost.academySession) return state;
  // Already lobby staff
  if (state.lobbyStaff.includes(ghostInstanceId)) return state;

  return {
    ...state,
    lobbyStaff: [...state.lobbyStaff, ghostInstanceId],
    ghosts: state.ghosts.map((g) =>
      g.id === ghostInstanceId ? { ...g, role: 'lobby_staff' as GhostRole } : g,
    ),
  };
});

// UNASSIGN_LOBBY_STAFF
executor.register('UNASSIGN_LOBBY_STAFF', (state, cmd: UnassignLobbyStaffCommand) => {
  const { ghostInstanceId } = cmd.payload;
  if (!state.lobbyStaff.includes(ghostInstanceId)) return state;

  return {
    ...state,
    lobbyStaff: state.lobbyStaff.filter((id) => id !== ghostInstanceId),
    ghosts: state.ghosts.map((g) =>
      g.id === ghostInstanceId ? { ...g, role: 'scare' as GhostRole } : g,
    ),
  };
});

// ── Facility Staff System ──────────────────────────────────

// ASSIGN_FACILITY_STAFF
executor.register('ASSIGN_FACILITY_STAFF', (state, cmd: AssignFacilityStaffCommand) => {
  const { ghostInstanceId, facilityId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  if (ghost.assignedRoomId || ghost.training || ghost.academySession) return state;
  if (state.lobbyStaff.includes(ghostInstanceId)) return state;

  const facility = state.facilities.find((f) => f.facilityId === facilityId);
  if (!facility) return state;

  // Unassign previous staff from this facility
  let newGhosts = state.ghosts;
  if (facility.assignedStaffId) {
    newGhosts = newGhosts.map((g) =>
      g.id === facility.assignedStaffId
        ? { ...g, role: 'scare' as GhostRole, assignedFacilityId: null }
        : g,
    );
  }

  // Unassign ghost from previous facility if any
  let newFacilities = state.facilities;
  if (ghost.assignedFacilityId) {
    newFacilities = newFacilities.map((f) =>
      f.facilityId === ghost.assignedFacilityId ? { ...f, assignedStaffId: null } : f,
    );
  }

  return {
    ...state,
    ghosts: newGhosts.map((g) =>
      g.id === ghostInstanceId
        ? { ...g, role: 'facility_staff' as GhostRole, assignedFacilityId: facilityId }
        : g,
    ),
    facilities: newFacilities.map((f) =>
      f.facilityId === facilityId ? { ...f, assignedStaffId: ghostInstanceId } : f,
    ),
  };
});

// UNASSIGN_FACILITY_STAFF
executor.register('UNASSIGN_FACILITY_STAFF', (state, cmd: UnassignFacilityStaffCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  if (!ghost.assignedFacilityId) return state;

  const facilityId = ghost.assignedFacilityId;

  return {
    ...state,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostInstanceId
        ? { ...g, role: 'scare' as GhostRole, assignedFacilityId: null }
        : g,
    ),
    facilities: state.facilities.map((f) =>
      f.facilityId === facilityId ? { ...f, assignedStaffId: null } : f,
    ),
  };
});

// ── Academy System ─────────────────────────────────────────

// START_ACADEMY_TRAINING
executor.register('START_ACADEMY_TRAINING', (state, cmd: StartAcademyTrainingCommand) => {
  const { ghostInstanceId, trainingId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;

  // Must be free
  if (ghost.assignedRoomId || ghost.training || ghost.academySession) return state;
  if (ghost.assignedFacilityId) return state;
  if (state.lobbyStaff.includes(ghostInstanceId)) return state;

  const trainingDef = ACADEMY_TRAINING_DEFS[trainingId];
  if (!trainingDef) return state;

  // Check current level
  const currentLevel = ghost.academySkills[trainingId];
  if (currentLevel >= trainingDef.maxLevel) return state;

  const cost = academyTrainingCost(trainingId, currentLevel);
  if (state.sp < cost) return state;

  const duration = academyTrainingDuration(trainingId, currentLevel);
  const now = Date.now();

  return {
    ...state,
    sp: state.sp - cost,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostInstanceId
        ? {
            ...g,
            academySession: {
              trainingId,
              startedAt: now,
              completesAt: now + duration,
            },
          }
        : g,
    ),
  };
});

// COMPLETE_ACADEMY_TRAINING
executor.register('COMPLETE_ACADEMY_TRAINING', (state, cmd: CompleteAcademyTrainingCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  if (!ghost.academySession) return state;

  const now = Date.now();
  if (now < ghost.academySession.completesAt) return state;

  const { trainingId } = ghost.academySession;
  const currentLevel = ghost.academySkills[trainingId];

  return {
    ...state,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostInstanceId
        ? {
            ...g,
            academySession: null,
            academySkills: {
              ...g.academySkills,
              [trainingId]: Math.min(currentLevel + 1, 5),
            },
            experience: g.experience + 50,
          }
        : g,
    ),
  };
});

// SKIP_ACADEMY_TRAINING (via ad)
executor.register('SKIP_ACADEMY_TRAINING', (state, cmd: SkipAcademyTrainingCommand) => {
  const { ghostInstanceId } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;
  if (!ghost.academySession) return state;

  const { trainingId } = ghost.academySession;
  const currentLevel = ghost.academySkills[trainingId];

  return {
    ...state,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostInstanceId
        ? {
            ...g,
            academySession: null,
            academySkills: {
              ...g.academySkills,
              [trainingId]: Math.min(currentLevel + 1, 5),
            },
            experience: g.experience + 50,
          }
        : g,
    ),
  };
});

// ── Lobby Building System (Unified: floors + facilities merged) ──

// Helper: check if ghost is free for lobby/facility assignment
function isGhostFree(state: GameStateData, ghostId: string): boolean {
  const ghost = state.ghosts.find((g) => g.id === ghostId);
  if (!ghost) return false;
  if (ghost.assignedRoomId || ghost.training || ghost.academySession) return false;
  if (ghost.assignedFacilityId) return false;
  if (state.lobbyStaff.includes(ghostId)) return false;
  // Check lobby building assignments (unified)
  if (state.lobbyBuilding.some((f) => f.assignedGhostId === ghostId)) return false;
  // Check facility building assignments
  if (state.facilityBuilding.some((f) => f.assignedGhostId === ghostId)) return false;
  // Check facility map plot assignments
  if (state.facilityMap && state.facilityMap.some((p) => p.facility?.assignedGhostId === ghostId)) return false;
  // Legacy lobby facilities check (for migration period)
  if (state.lobbyFacilities && state.lobbyFacilities.some((f) => f.assignedStaffId === ghostId)) return false;
  // Check lobby NPC ghost assignments
  if (state.lobbyNpcGhosts && state.lobbyNpcGhosts.some((ng) => ng.ghostId === ghostId)) return false;
  return true;
}

// ADD_LOBBY_FLOOR: Add a new empty floor to the lobby building
executor.register('ADD_LOBBY_FLOOR', (state, _cmd: AddLobbyFloorCommand) => {
  const addCost = 500 * state.lobbyBuilding.length; // Increasing cost per floor
  if (state.sp < addCost) return state;

  const newLevel = state.lobbyBuilding.length + 1;
  const newFloor: LobbyFloor = {
    level: newLevel,
    facilityType: null,
    facilityLevel: 0,
    assignedGhostId: null,
    props: [],
  };

  return {
    ...state,
    sp: state.sp - addCost,
    lobbyBuilding: [...state.lobbyBuilding, newFloor],
  };
});

// BUILD_LOBBY_FLOOR_FACILITY: Build a facility on an empty lobby floor
executor.register('BUILD_LOBBY_FLOOR_FACILITY', (state, cmd: BuildLobbyFloorFacilityCommand) => {
  const { level, facilityType } = cmd.payload;
  // Floor 1 is reserved for ticket booth — cannot build anything else
  if (level === 1) return state;
  const floor = state.lobbyBuilding.find((f) => f.level === level);
  if (!floor) return state;
  // Floor must be empty
  if (floor.facilityType !== null) return state;

  const def = LOBBY_FACILITY_DEFS[facilityType];
  if (!def) return state;

  // Check if this facility type is already built on another floor
  if (state.lobbyBuilding.some((f) => f.facilityType === facilityType)) return state;

  if (state.sp < def.cost) return state;

  return {
    ...state,
    sp: state.sp - def.cost,
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === level ? { ...f, facilityType, facilityLevel: 1 } : f,
    ),
  };
});

// BUILD_LOBBY_FLOOR (legacy: redirects to BUILD_LOBBY_FLOOR_FACILITY)
executor.register('BUILD_LOBBY_FLOOR', (state, cmd: BuildLobbyFloorCommand) => {
  const { roomType } = cmd.payload;
  // Map legacy room types to facility types
  const facilityType = roomType as unknown as LobbyFacilityType;
  const def = LOBBY_FACILITY_DEFS[facilityType];
  if (!def) return state;

  // Find first empty floor
  const emptyFloor = state.lobbyBuilding.find((f) => f.facilityType === null);
  if (!emptyFloor) return state;

  // Check if already built
  if (state.lobbyBuilding.some((f) => f.facilityType === facilityType)) return state;

  if (state.sp < def.cost) return state;

  return {
    ...state,
    sp: state.sp - def.cost,
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === emptyFloor.level ? { ...f, facilityType, facilityLevel: 1 } : f,
    ),
  };
});

// UPGRADE_LOBBY_FLOOR: Upgrade the facility on a lobby floor
executor.register('UPGRADE_LOBBY_FLOOR', (state, cmd: UpgradeLobbyFloorCommand) => {
  const { level } = cmd.payload;
  const floor = state.lobbyBuilding.find((f) => f.level === level);
  if (!floor || !floor.facilityType) return state;

  const def = LOBBY_FACILITY_DEFS[floor.facilityType];
  if (!def) return state;
  if (floor.facilityLevel >= def.maxLevel) return state;

  const cost = lobbyFacilityUpgradeCost(floor.facilityType, floor.facilityLevel + 1);
  if (state.sp < cost) return state;

  return {
    ...state,
    sp: state.sp - cost,
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === level ? { ...f, facilityLevel: f.facilityLevel + 1 } : f,
    ),
  };
});

// ASSIGN_LOBBY_GHOST: Assign ghost NPC to a lobby floor
executor.register('ASSIGN_LOBBY_GHOST', (state, cmd: AssignLobbyGhostCommand) => {
  const { ghostInstanceId, floorLevel } = cmd.payload;
  const floor = state.lobbyBuilding.find((f) => f.level === floorLevel);
  if (!floor) return state;

  if (!isGhostFree(state, ghostInstanceId)) return state;

  // Unassign previous ghost from this floor
  let newGhosts = state.ghosts;
  if (floor.assignedGhostId) {
    newGhosts = newGhosts.map((g) =>
      g.id === floor.assignedGhostId ? { ...g, role: 'scare' as GhostRole } : g,
    );
  }

  return {
    ...state,
    ghosts: newGhosts.map((g) =>
      g.id === ghostInstanceId ? { ...g, role: 'lobby_staff' as GhostRole } : g,
    ),
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === floorLevel ? { ...f, assignedGhostId: ghostInstanceId } : f,
    ),
  };
});

// UNASSIGN_LOBBY_GHOST
executor.register('UNASSIGN_LOBBY_GHOST', (state, cmd: UnassignLobbyGhostCommand) => {
  const { floorLevel } = cmd.payload;
  const floor = state.lobbyBuilding.find((f) => f.level === floorLevel);
  if (!floor || !floor.assignedGhostId) return state;

  const ghostId = floor.assignedGhostId;
  return {
    ...state,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostId ? { ...g, role: 'scare' as GhostRole } : g,
    ),
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === floorLevel ? { ...f, assignedGhostId: null } : f,
    ),
  };
});

// ASSIGN_LOBBY_FLOOR_NPC: Same as ASSIGN_LOBBY_GHOST but via new command name
executor.register('ASSIGN_LOBBY_FLOOR_NPC', (state, cmd: AssignLobbyFloorNpcCommand) => {
  const { level, ghostInstanceId } = cmd.payload;
  const floor = state.lobbyBuilding.find((f) => f.level === level);
  if (!floor || !floor.facilityType) return state;

  if (!isGhostFree(state, ghostInstanceId)) return state;

  let newGhosts = state.ghosts;
  if (floor.assignedGhostId) {
    newGhosts = newGhosts.map((g) =>
      g.id === floor.assignedGhostId ? { ...g, role: 'scare' as GhostRole } : g,
    );
  }

  return {
    ...state,
    ghosts: newGhosts.map((g) =>
      g.id === ghostInstanceId ? { ...g, role: 'lobby_staff' as GhostRole } : g,
    ),
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === level ? { ...f, assignedGhostId: ghostInstanceId } : f,
    ),
  };
});

// UNASSIGN_LOBBY_FLOOR_NPC
executor.register('UNASSIGN_LOBBY_FLOOR_NPC', (state, cmd: UnassignLobbyFloorNpcCommand) => {
  const { level } = cmd.payload;
  const floor = state.lobbyBuilding.find((f) => f.level === level);
  if (!floor || !floor.assignedGhostId) return state;

  const ghostId = floor.assignedGhostId;
  return {
    ...state,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostId ? { ...g, role: 'scare' as GhostRole } : g,
    ),
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === level ? { ...f, assignedGhostId: null } : f,
    ),
  };
});

// BUY_LOBBY_PROP
executor.register('BUY_LOBBY_PROP', (state, cmd: BuyLobbyPropCommand) => {
  const { floorLevel, propId } = cmd.payload;
  const floor = state.lobbyBuilding.find((f) => f.level === floorLevel);
  if (!floor) return state;

  const propDef = LOBBY_PROP_DEFS[propId];
  if (!propDef) return state;

  const existing = floor.props.find((p) => p.propId === propId);
  const currentLevel = existing ? existing.level : 0;
  const nextLevel = currentLevel + 1;
  if (nextLevel > propDef.maxLevel) return state;

  const cost = lobbyPropCostForLevel(propId, nextLevel);
  if (state.sp < cost) return state;

  const newProps: LobbyProp[] = existing
    ? floor.props.map((p) => p.propId === propId ? { ...p, level: nextLevel } : p)
    : [...floor.props, { propId, level: 1 }];

  return {
    ...state,
    sp: state.sp - cost,
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === floorLevel ? { ...f, props: newProps } : f,
    ),
  };
});

// DEMOLISH_LOBBY_FACILITY_FLOOR: Remove facility from a lobby floor (floor stays, becomes empty)
executor.register('DEMOLISH_LOBBY_FACILITY_FLOOR', (state, cmd: DemolishLobbyFacilityCommand) => {
  const { level } = cmd.payload;
  // Floor 1 ticket booth is permanent — cannot demolish
  if (level === 1) return state;
  const floor = state.lobbyBuilding.find((f) => f.level === level);
  if (!floor || !floor.facilityType) return state;

  // Unassign ghost if any
  const ghostId = floor.assignedGhostId;

  return {
    ...state,
    ghosts: ghostId
      ? state.ghosts.map((g) =>
          g.id === ghostId ? { ...g, role: 'scare' as GhostRole, assignedFacilityId: null } : g,
        )
      : state.ghosts,
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === level
        ? { ...f, facilityType: null, facilityLevel: 0, assignedGhostId: null, props: [] }
        : f,
    ),
  };
});

// ── Legacy Lobby Facility Commands (redirect to unified system) ──

// BUILD_LOBBY_FACILITY (legacy: builds on first empty floor)
executor.register('BUILD_LOBBY_FACILITY', (state, cmd: BuildLobbyFacilityCommand) => {
  const { facilityType } = cmd.payload;
  const def = LOBBY_FACILITY_DEFS[facilityType];
  if (!def) return state;

  // Check if already built on any floor
  if (state.lobbyBuilding.some((f) => f.facilityType === facilityType)) return state;

  // Find first empty floor
  const emptyFloor = state.lobbyBuilding.find((f) => f.facilityType === null);
  if (!emptyFloor) return state;

  if (state.sp < def.cost) return state;

  return {
    ...state,
    sp: state.sp - def.cost,
    lobbyBuilding: state.lobbyBuilding.map((f) =>
      f.level === emptyFloor.level ? { ...f, facilityType, facilityLevel: 1 } : f,
    ),
  };
});

// UPGRADE_LOBBY_FACILITY (legacy: find floor by facilityId match or type)
executor.register('UPGRADE_LOBBY_FACILITY', (state, _cmd: UpgradeLobbyFacilityCommand) => {
  // Legacy: facilityId was an instance ID. Now find floor by matching.
  // Since old save data may still have lobbyFacilities, just no-op.
  return state;
});

// ASSIGN_LOBBY_FACILITY_NPC (legacy: redirect to floor NPC assignment)
executor.register('ASSIGN_LOBBY_FACILITY_NPC', (state, _cmd: AssignLobbyFacilityNpcCommand) => {
  // Legacy no-op: use ASSIGN_LOBBY_FLOOR_NPC instead
  return state;
});

// UNASSIGN_LOBBY_FACILITY_NPC (legacy: no-op)
executor.register('UNASSIGN_LOBBY_FACILITY_NPC', (state, _cmd: UnassignLobbyFacilityNpcCommand) => {
  return state;
});

// ── Facility Building System ───────────────────────────────

// BUILD_FACILITY_FLOOR
executor.register('BUILD_FACILITY_FLOOR', (state, cmd: BuildFacilityFloorCommand) => {
  const { facilityId } = cmd.payload;
  const facilityDef = FACILITY_DEFS[facilityId];
  if (!facilityDef) return state;

  // Check if already built as a floor
  if (state.facilityBuilding.some((f) => f.type === facilityId)) return state;

  if (state.sp < facilityDef.buildCost) return state;

  const newFloor: FacilityFloor = {
    level: state.facilityBuilding.length + 1,
    type: facilityId,
    upgradeLevel: 1,
    assignedGhostId: null,
    props: [],
  };

  // Also add to legacy facilities array for backward compatibility
  const legacyExists = state.facilities.some((f) => f.facilityId === facilityId);
  const newFacilities = legacyExists ? state.facilities : [
    ...state.facilities,
    { facilityId, level: 1, assignedStaffId: null } as FacilityInstance,
  ];

  return {
    ...state,
    sp: state.sp - facilityDef.buildCost,
    facilityBuilding: [...state.facilityBuilding, newFloor],
    facilities: newFacilities,
  };
});

// UPGRADE_FACILITY_FLOOR
executor.register('UPGRADE_FACILITY_FLOOR', (state, cmd: UpgradeFacilityFloorCommand) => {
  const { level } = cmd.payload;
  const floor = state.facilityBuilding.find((f) => f.level === level);
  if (!floor) return state;

  const facilityDef = FACILITY_DEFS[floor.type];
  if (!facilityDef) return state;
  if (floor.upgradeLevel >= facilityDef.maxLevel) return state;

  const nextLevel = floor.upgradeLevel + 1;
  const cost = facilityCostForLevel(floor.type, nextLevel);
  if (state.sp < cost) return state;

  // Also upgrade legacy facility
  return {
    ...state,
    sp: state.sp - cost,
    facilityBuilding: state.facilityBuilding.map((f) =>
      f.level === level ? { ...f, upgradeLevel: nextLevel } : f,
    ),
    facilities: state.facilities.map((f) =>
      f.facilityId === floor.type ? { ...f, level: nextLevel } : f,
    ),
  };
});

// ASSIGN_FACILITY_GHOST
executor.register('ASSIGN_FACILITY_GHOST', (state, cmd: AssignFacilityGhostCommand) => {
  const { ghostInstanceId, floorLevel } = cmd.payload;
  const floor = state.facilityBuilding.find((f) => f.level === floorLevel);
  if (!floor) return state;

  if (!isGhostFree(state, ghostInstanceId)) return state;

  // Unassign previous ghost from this floor
  let newGhosts = state.ghosts;
  if (floor.assignedGhostId) {
    newGhosts = newGhosts.map((g) =>
      g.id === floor.assignedGhostId
        ? { ...g, role: 'scare' as GhostRole, assignedFacilityId: null }
        : g,
    );
  }

  return {
    ...state,
    ghosts: newGhosts.map((g) =>
      g.id === ghostInstanceId
        ? { ...g, role: 'facility_staff' as GhostRole, assignedFacilityId: floor.type }
        : g,
    ),
    facilityBuilding: state.facilityBuilding.map((f) =>
      f.level === floorLevel ? { ...f, assignedGhostId: ghostInstanceId } : f,
    ),
    facilities: state.facilities.map((f) =>
      f.facilityId === floor.type ? { ...f, assignedStaffId: ghostInstanceId } : f,
    ),
  };
});

// UNASSIGN_FACILITY_GHOST
executor.register('UNASSIGN_FACILITY_GHOST', (state, cmd: UnassignFacilityGhostCommand) => {
  const { floorLevel } = cmd.payload;
  const floor = state.facilityBuilding.find((f) => f.level === floorLevel);
  if (!floor || !floor.assignedGhostId) return state;

  const ghostId = floor.assignedGhostId;
  return {
    ...state,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostId
        ? { ...g, role: 'scare' as GhostRole, assignedFacilityId: null }
        : g,
    ),
    facilityBuilding: state.facilityBuilding.map((f) =>
      f.level === floorLevel ? { ...f, assignedGhostId: null } : f,
    ),
    facilities: state.facilities.map((f) =>
      f.facilityId === floor.type ? { ...f, assignedStaffId: null } : f,
    ),
  };
});

// BUY_FACILITY_PROP
executor.register('BUY_FACILITY_PROP', (state, cmd: BuyFacilityPropCommand) => {
  const { floorLevel, propId } = cmd.payload;
  const floor = state.facilityBuilding.find((f) => f.level === floorLevel);
  if (!floor) return state;

  const propDef = FACILITY_PROP_DEFS[propId];
  if (!propDef) return state;

  // Check if prop is allowed for this facility type
  if (!propDef.allowedFacilities.includes(floor.type)) return state;

  const existing = floor.props.find((p) => p.propId === propId);
  const currentLevel = existing ? existing.level : 0;
  const nextLevel = currentLevel + 1;
  if (nextLevel > propDef.maxLevel) return state;

  const cost = facilityPropCostForLevel(propId, nextLevel);
  if (state.sp < cost) return state;

  const newProps: FacilityProp[] = existing
    ? floor.props.map((p) => p.propId === propId ? { ...p, level: nextLevel } : p)
    : [...floor.props, { propId, level: 1 }];

  return {
    ...state,
    sp: state.sp - cost,
    facilityBuilding: state.facilityBuilding.map((f) =>
      f.level === floorLevel ? { ...f, props: newProps } : f,
    ),
  };
});

// ── Facility Map (Plot-based grid) System ──────────────────

/** SP cost to unlock a plot by its index */
export function plotUnlockCost(plotId: number): number {
  // First 4 plots are free (unlocked by default).
  // Plots 4-15 cost increasingly more (further down the street = pricier).
  const baseCost = 2000;
  const tier = Math.max(0, plotId - 3); // 0-based beyond first 4
  return Math.floor(baseCost * Math.pow(1.6, tier));
}

// BUILD_FACILITY_ON_PLOT
executor.register('BUILD_FACILITY_ON_PLOT', (state, cmd: BuildFacilityOnPlotCommand) => {
  const { plotId, facilityType } = cmd.payload;
  const plot = state.facilityMap?.[plotId];
  if (!plot || !plot.unlocked || plot.facility) return state;

  const facilityDef = FACILITY_DEFS[facilityType];
  if (!facilityDef) return state;

  // Allow multiple of the same facility type (no uniqueness check)

  if (state.sp < facilityDef.buildCost) return state;

  // Also sync to legacy facilities array
  const legacyExists = state.facilities.some((f) => f.facilityId === facilityType);
  const newFacilities = legacyExists ? state.facilities : [
    ...state.facilities,
    { facilityId: facilityType, level: 1, assignedStaffId: null } as FacilityInstance,
  ];

  // Default price = spPerTick * 2 (reasonable starting price)
  const defaultPrice = facilityDef.spPerTick > 0 ? facilityDef.spPerTick * 2 : 0;

  // For haunted_house type: auto-link to next available building meta
  let buildingId: string | undefined;
  let newBuildingMetas = state.hauntedBuildingMetas;
  let newHauntedBuildings = state.hauntedBuildings;

  if (facilityType === 'haunted_house') {
    // Find already-placed building IDs
    const placedIds = new Set(
      state.facilityMap
        .filter(p => p.facility?.type === 'haunted_house' && p.facility.buildingId)
        .map(p => p.facility!.buildingId!),
    );
    // Find next unplaced & unlocked building meta
    const nextMeta = state.hauntedBuildingMetas.find(m => m.isUnlocked && !placedIds.has(m.id));
    if (nextMeta) {
      buildingId = nextMeta.id;
    } else {
      // Find next locked building meta and auto-unlock it
      const lockedMeta = state.hauntedBuildingMetas.find(m => !m.isUnlocked && !placedIds.has(m.id));
      if (lockedMeta) {
        buildingId = lockedMeta.id;
        newBuildingMetas = state.hauntedBuildingMetas.map(m =>
          m.id === lockedMeta.id ? { ...m, isUnlocked: true } : m,
        );
        // Create a new building state for the unlocked meta
        const newBuildingState = createInitialHauntedBuildingState();
        newBuildingState.id = lockedMeta.id;
        newHauntedBuildings = [...state.hauntedBuildings, newBuildingState];
      }
    }
  }

  return {
    ...state,
    sp: state.sp - facilityDef.buildCost,
    hauntedBuildingMetas: newBuildingMetas,
    hauntedBuildings: newHauntedBuildings,
    facilityMap: state.facilityMap.map((p) =>
      p.id === plotId
        ? { ...p, facility: {
            type: facilityType, upgradeLevel: 1, assignedGhostId: null,
            props: [], price: defaultPrice, totalRevenue: 0, isOpen: false,
            totalRatings: 0, ratingSum: 0, visitorCount: 0,
            ...(buildingId ? { buildingId } : {}),
          } }
        : p,
    ),
    facilities: newFacilities,
  };
});

// UPGRADE_FACILITY_PLOT
executor.register('UPGRADE_FACILITY_PLOT', (state, cmd: UpgradeFacilityPlotCommand) => {
  const { plotId } = cmd.payload;
  const plot = state.facilityMap?.[plotId];
  if (!plot || !plot.facility) return state;

  const facilityDef = FACILITY_DEFS[plot.facility.type];
  if (!facilityDef) return state;
  if (plot.facility.upgradeLevel >= facilityDef.maxLevel) return state;

  const nextLevel = plot.facility.upgradeLevel + 1;
  const cost = facilityCostForLevel(plot.facility.type, nextLevel);
  if (state.sp < cost) return state;

  return {
    ...state,
    sp: state.sp - cost,
    facilityMap: state.facilityMap.map((p) =>
      p.id === plotId && p.facility
        ? { ...p, facility: { ...p.facility, upgradeLevel: nextLevel } }
        : p,
    ),
    facilities: state.facilities.map((f) =>
      f.facilityId === plot.facility!.type ? { ...f, level: nextLevel } : f,
    ),
  };
});

// ASSIGN_FACILITY_PLOT_STAFF
executor.register('ASSIGN_FACILITY_PLOT_STAFF', (state, cmd: AssignFacilityPlotStaffCommand) => {
  const { plotId, ghostInstanceId } = cmd.payload;
  const plot = state.facilityMap?.[plotId];
  if (!plot || !plot.facility) return state;

  if (!isGhostFree(state, ghostInstanceId)) return state;

  // Unassign previous ghost from this plot
  let newGhosts = state.ghosts;
  if (plot.facility.assignedGhostId) {
    newGhosts = newGhosts.map((g) =>
      g.id === plot.facility!.assignedGhostId
        ? { ...g, role: 'scare' as GhostRole, assignedFacilityId: null }
        : g,
    );
  }

  return {
    ...state,
    ghosts: newGhosts.map((g) =>
      g.id === ghostInstanceId
        ? { ...g, role: 'facility_staff' as GhostRole, assignedFacilityId: plot.facility!.type }
        : g,
    ),
    facilityMap: state.facilityMap.map((p) =>
      p.id === plotId && p.facility
        ? { ...p, facility: { ...p.facility, assignedGhostId: ghostInstanceId } }
        : p,
    ),
    facilities: state.facilities.map((f) =>
      f.facilityId === plot.facility!.type ? { ...f, assignedStaffId: ghostInstanceId } : f,
    ),
  };
});

// UNASSIGN_FACILITY_PLOT_STAFF
executor.register('UNASSIGN_FACILITY_PLOT_STAFF', (state, cmd: UnassignFacilityPlotStaffCommand) => {
  const { plotId } = cmd.payload;
  const plot = state.facilityMap?.[plotId];
  if (!plot || !plot.facility || !plot.facility.assignedGhostId) return state;

  const ghostId = plot.facility.assignedGhostId;

  return {
    ...state,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostId
        ? { ...g, role: 'scare' as GhostRole, assignedFacilityId: null }
        : g,
    ),
    facilityMap: state.facilityMap.map((p) =>
      p.id === plotId && p.facility
        ? { ...p, facility: { ...p.facility, assignedGhostId: null, isOpen: p.facility.type === 'haunted_house' ? p.facility.isOpen : false } }
        : p,
    ),
    facilities: state.facilities.map((f) =>
      f.facilityId === plot.facility!.type ? { ...f, assignedStaffId: null } : f,
    ),
  };
});

// UNLOCK_FACILITY_PLOT
executor.register('UNLOCK_FACILITY_PLOT', (state, cmd: UnlockFacilityPlotCommand) => {
  const { plotId } = cmd.payload;
  const plot = state.facilityMap?.[plotId];
  if (!plot || plot.unlocked) return state;

  const cost = plotUnlockCost(plotId);
  if (state.sp < cost) return state;

  return {
    ...state,
    sp: state.sp - cost,
    facilityMap: state.facilityMap.map((p) =>
      p.id === plotId ? { ...p, unlocked: true } : p,
    ),
  };
});

// SET_FACILITY_PRICE
executor.register('SET_FACILITY_PRICE', (state, cmd: SetFacilityPriceCommand) => {
  const { plotId, price } = cmd.payload;
  const plot = state.facilityMap?.[plotId];
  if (!plot || !plot.facility) return state;
  if (price < 0) return state;

  return {
    ...state,
    facilityMap: state.facilityMap.map((p) =>
      p.id === plotId && p.facility
        ? { ...p, facility: { ...p.facility, price } }
        : p,
    ),
  };
});

// DEMOLISH_FACILITY
executor.register('DEMOLISH_FACILITY', (state, cmd: DemolishFacilityCommand) => {
  const { plotId } = cmd.payload;
  const plot = state.facilityMap?.[plotId];
  if (!plot || !plot.facility) return state;

  // Unassign ghost if present
  const ghostId = plot.facility.assignedGhostId;

  return {
    ...state,
    ghosts: ghostId
      ? state.ghosts.map((g) =>
          g.id === ghostId ? { ...g, role: 'scare' as const, assignedFacilityId: null } : g,
        )
      : state.ghosts,
    facilityMap: state.facilityMap.map((p) =>
      p.id === plotId ? { ...p, facility: null } : p,
    ),
  };
});

// MOVE_FACILITY
executor.register('MOVE_FACILITY', (state, cmd: MoveFacilityCommand) => {
  const { fromPlotId, toPlotId } = cmd.payload;
  const fromPlot = state.facilityMap?.[fromPlotId];
  const toPlot = state.facilityMap?.[toPlotId];
  if (!fromPlot || !fromPlot.facility) return state;
  if (!toPlot || !toPlot.unlocked || toPlot.facility) return state;

  const facility = fromPlot.facility;

  return {
    ...state,
    facilityMap: state.facilityMap.map((p) => {
      if (p.id === fromPlotId) return { ...p, facility: null };
      if (p.id === toPlotId) return { ...p, facility: { ...facility } };
      return p;
    }),
  };
});

// TOGGLE_FACILITY_OPEN - open or close a specific facility on a plot
executor.register('TOGGLE_FACILITY_OPEN', (state, cmd: ToggleFacilityOpenCommand) => {
  const { plotId } = cmd.payload;
  const plot = state.facilityMap?.[plotId];
  if (!plot || !plot.facility) return state;

  const wantsOpen = !plot.facility.isOpen;

  // Cannot open without assigned staff (haunted_house exempt — uses building's ghosts)
  if (wantsOpen && !plot.facility.assignedGhostId && plot.facility.type !== 'haunted_house') {
    return state; // block opening — needs staff
  }

  return {
    ...state,
    facilityMap: state.facilityMap.map((p) => {
      if (p.id === plotId && p.facility) {
        return { ...p, facility: { ...p.facility, isOpen: wantsOpen } };
      }
      return p;
    }),
  };
});

// ── Room Condition / Hygiene System ────────────────────────

/** Cost constants for repair/cleaning */
const REPAIR_COST_BASE = 200;
const CLEANING_COST = 100;
const REPAIR_DURATION_MS = 30000;
const CLEANING_DURATION_MS = 20000;
const CONDITION_UPGRADE_MAX = 20;
const HYGIENE_UPGRADE_MAX = 20;

function conditionUpgradeCost(level: number): number {
  return Math.round(500 * Math.pow(1.5, level));
}

function hygieneUpgradeCost(level: number): number {
  return Math.round(400 * Math.pow(1.5, level));
}

// REQUEST_REPAIR
executor.register('REQUEST_REPAIR', (state, cmd: RequestRepairCommand) => {
  const { floorLevel } = cmd.payload;
  const floor = state.building.find((f) => f.level === floorLevel);
  if (!floor || !floor.room) return state;
  const room = floor.room;

  if (room.underRepair || room.underCleaning) return state;
  if (room.condition > 80 && !room.collapsed) return state;

  // Calculate cost: base cost, reduced by 30% if conditionUpgrade >= 10
  const hasSturdyBonus = room.conditionUpgrade >= 10;
  const cost = hasSturdyBonus ? Math.round(REPAIR_COST_BASE * 0.7) : REPAIR_COST_BASE;
  if (state.sp < cost) return state;

  const now = Date.now();
  return {
    ...state,
    sp: state.sp - cost,
    building: state.building.map((f) =>
      f.level === floorLevel && f.room
        ? {
            ...f,
            room: {
              ...f.room,
              underRepair: true,
              repairEndsAt: now + REPAIR_DURATION_MS,
            },
          }
        : f,
    ),
  };
});

// REQUEST_CLEANING
executor.register('REQUEST_CLEANING', (state, cmd: RequestCleaningCommand) => {
  const { floorLevel } = cmd.payload;
  const floor = state.building.find((f) => f.level === floorLevel);
  if (!floor || !floor.room) return state;
  const room = floor.room;

  if (room.underCleaning || room.underRepair) return state;
  if (room.hygiene > 80) return state;

  if (state.sp < CLEANING_COST) return state;

  const now = Date.now();
  return {
    ...state,
    sp: state.sp - CLEANING_COST,
    building: state.building.map((f) =>
      f.level === floorLevel && f.room
        ? {
            ...f,
            room: {
              ...f.room,
              underCleaning: true,
              cleaningEndsAt: now + CLEANING_DURATION_MS,
            },
          }
        : f,
    ),
  };
});

// UPGRADE_CONDITION (Structural Reinforcement)
executor.register('UPGRADE_CONDITION', (state, cmd: UpgradeConditionCommand) => {
  const { floorLevel } = cmd.payload;
  const floor = state.building.find((f) => f.level === floorLevel);
  if (!floor || !floor.room) return state;
  const room = floor.room;

  const currentLevel = room.conditionUpgrade ?? 0;
  if (currentLevel >= CONDITION_UPGRADE_MAX) return state;

  const cost = conditionUpgradeCost(currentLevel);
  if (state.sp < cost) return state;

  return {
    ...state,
    sp: state.sp - cost,
    building: state.building.map((f) =>
      f.level === floorLevel && f.room
        ? {
            ...f,
            room: {
              ...f.room,
              conditionUpgrade: currentLevel + 1,
            },
          }
        : f,
    ),
  };
});

// UPGRADE_HYGIENE (Auto Purification)
executor.register('UPGRADE_HYGIENE', (state, cmd: UpgradeHygieneCommand) => {
  const { floorLevel } = cmd.payload;
  const floor = state.building.find((f) => f.level === floorLevel);
  if (!floor || !floor.room) return state;
  const room = floor.room;

  const currentLevel = room.hygieneUpgrade ?? 0;
  if (currentLevel >= HYGIENE_UPGRADE_MAX) return state;

  const cost = hygieneUpgradeCost(currentLevel);
  if (state.sp < cost) return state;

  return {
    ...state,
    sp: state.sp - cost,
    building: state.building.map((f) =>
      f.level === floorLevel && f.room
        ? {
            ...f,
            room: {
              ...f.room,
              hygieneUpgrade: currentLevel + 1,
            },
          }
        : f,
    ),
  };
});

// DEMOLISH_ROOM
executor.register('DEMOLISH_ROOM', (state, cmd: DemolishRoomCommand) => {
  const { floorLevel } = cmd.payload;
  const floor = state.building.find((f) => f.level === floorLevel);
  if (!floor || !floor.room) return state;

  const room = floor.room;

  // Unassign ghost if present
  let newGhosts = state.ghosts;
  if (room.assignedGhostId) {
    newGhosts = state.ghosts.map((g) =>
      g.id === room.assignedGhostId ? { ...g, assignedRoomId: null } : g,
    );
  }

  return {
    ...state,
    ghosts: newGhosts,
    building: state.building.map((f) =>
      f.level === floorLevel ? { ...f, room: null } : f,
    ),
  };
});

// ── Day Phase Cycle Commands ───────────────────────────────

// START_EVENING - transition from day phase to evening (open for business)
executor.register('START_EVENING', (state, _cmd: StartEveningCommand) => {
  if (state.dayPhase !== 'day') return state;

  const now = Date.now();

  // Generate daily milestones if not yet generated
  let milestones = state.dailyMilestones;
  if (milestones.length === 0) {
    const milestonePool: Array<{ nameKey: string; target: number; reward: number }> = [
      { nameKey: 'milestone_visitors_5', target: 5, reward: 200 },
      { nameKey: 'milestone_visitors_10', target: 10, reward: 500 },
      { nameKey: 'milestone_visitors_20', target: 20, reward: 1500 },
      { nameKey: 'milestone_earnings_1000', target: 1000, reward: 300 },
      { nameKey: 'milestone_earnings_5000', target: 5000, reward: 800 },
      { nameKey: 'milestone_earnings_10000', target: 10000, reward: 2000 },
      { nameKey: 'milestone_flee_0', target: 0, reward: 500 },
      { nameKey: 'milestone_flee_1', target: 1, reward: 300 },
      { nameKey: 'milestone_rooms_3', target: 3, reward: 400 },
      { nameKey: 'milestone_rooms_5', target: 5, reward: 800 },
      { nameKey: 'milestone_ghosts_2', target: 2, reward: 300 },
      { nameKey: 'milestone_ghosts_3', target: 3, reward: 600 },
    ];
    const shuffled = [...milestonePool].sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, 5);
    milestones = picked.map((m, i) => ({
      id: `milestone-${now}-${i}`,
      nameKey: m.nameKey,
      target: m.target,
      current: 0,
      completed: false,
      reward: m.reward,
      collected: false,
    }));
  }

  // Spawn 3 initial street visitors on park open
  const availableThemes: string[] = [];
  for (const plot of state.facilityMap) {
    if (plot.facility?.type === 'haunted_house' && plot.facility.buildingId) {
      const meta = (state.hauntedBuildingMetas || []).find(m => m.id === plot.facility!.buildingId);
      if (meta?.themeId) availableThemes.push(meta.themeId);
    }
  }
  const initialVisitors = [
    { ...createStreetVisitor(availableThemes), avatarIdx: 0 },
    { ...createStreetVisitor(availableThemes), avatarIdx: 1 },
    { ...createStreetVisitor(availableThemes), avatarIdx: 2 },
  ];

  return {
    ...state,
    dayPhase: 'evening' as const,
    dayPhaseStartedAt: now,
    isOpen: true,
    dayStartTime: now,
    dailyMilestones: milestones,
    streetVisitors: [...(state.streetVisitors || []), ...initialVisitors],
  };
});

// SET_GAME_SPEED - toggle between 1x and 2x during evening
executor.register('SET_GAME_SPEED', (state, cmd: SetGameSpeedCommand) => {
  const { speed } = cmd.payload;
  if (speed !== 1 && speed !== 2) return state;
  return { ...state, gameSpeed: speed };
});

// ADVANCE_TO_NIGHT - end evening, enter night (Day Summary)
executor.register('ADVANCE_TO_NIGHT', (state, _cmd: AdvanceToNightCommand) => {
  if (state.dayPhase !== 'evening') return state;

  // Calculate total salaries
  let totalSalaries = 0;
  for (const staff of (state.ghostStaff || [])) {
    totalSalaries += staff.salary;
  }

  return {
    ...state,
    dayPhase: 'night' as const,
    dayPhaseStartedAt: Date.now(),
    isOpen: false,
    gameSpeed: 1,
    todaySalaries: totalSalaries,
  };
});

// ADVANCE_TO_DAY - from night summary to next day's day phase
executor.register('ADVANCE_TO_DAY', (state, _cmd: AdvanceToDayCommand) => {
  if (state.dayPhase !== 'night') return state;

  // Deduct salaries
  const salaries = state.todaySalaries || 0;
  const newSp = Math.max(0, state.sp - salaries);

  return {
    ...state,
    dayPhase: 'day' as const,
    dayPhaseStartedAt: Date.now(),
    isOpen: false,
    dayNumber: state.dayNumber + 1,
    todayVisitors: 0,
    todayEarnings: 0,
    dayStartTime: 0,
    todayFleeCount: 0,
    timePhase: 'day' as const,
    timePhaseStartAt: 0,
    dailyMilestones: [],
    gameSpeed: 1,
    sp: newSp,
    todaySalaries: 0,
    // Clear current visitor group and queue
    visitorGroup: null,
    lobbyWaitQueue: [],
    queueTickAccumMs: 0,
    queueSpawnAccumMs: 0,
  };
});

// ── Human Staff Commands ───────────────────────────────────

const GHOST_STAFF_NAMES = [
  'Wisp', 'Shade', 'Phantom', 'Specter', 'Wraith', 'Echo', 'Mist', 'Flicker',
  'Gloom', 'Haunt', 'Drift', 'Vapor', 'Ember', 'Fade', 'Chill', 'Dusk',
];

// HIRE_GHOST_STAFF
executor.register('HIRE_GHOST_STAFF', (state, cmd: HireGhostStaffCommand) => {
  const { staffType } = cmd.payload;
  const def = GHOST_STAFF_DEFS[staffType];
  if (!def) return state;
  if (state.sp < def.hireCost) return state;

  const name = GHOST_STAFF_NAMES[Math.floor(Math.random() * GHOST_STAFF_NAMES.length)];
  const newStaff: GhostStaff = {
    id: generateId(),
    type: staffType,
    name,
    level: 1,
    salary: def.salary,
    assignedTo: null,
    hiredAt: state.dayNumber,
  };

  return {
    ...state,
    sp: state.sp - def.hireCost,
    ghostStaff: [...(state.ghostStaff || []), newStaff],
  };
});

// FIRE_GHOST_STAFF
executor.register('FIRE_GHOST_STAFF', (state, cmd: FireGhostStaffCommand) => {
  const { staffId } = cmd.payload;
  const staff = (state.ghostStaff || []).find((s) => s.id === staffId);
  if (!staff) return state;

  return {
    ...state,
    ghostStaff: (state.ghostStaff || []).filter((s) => s.id !== staffId),
  };
});

// SET_GHOST_ROLE
executor.register('SET_GHOST_ROLE', (state, cmd: SetGhostRoleCommand) => {
  const { ghostInstanceId, role } = cmd.payload;
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return state;

  return {
    ...state,
    ghosts: state.ghosts.map((g) =>
      g.id === ghostInstanceId ? { ...g, role } : g,
    ),
  };
});

// ASSIGN_GHOST_STAFF
executor.register('ASSIGN_GHOST_STAFF', (state, cmd: AssignGhostStaffCommand) => {
  const { staffId, target } = cmd.payload;
  const staff = (state.ghostStaff || []).find((s) => s.id === staffId);
  if (!staff) return state;

  return {
    ...state,
    ghostStaff: (state.ghostStaff || []).map((s) =>
      s.id === staffId ? { ...s, assignedTo: target } : s,
    ),
  };
});

// ── Lobby NPC Ghost System ─────────────────────────────────

const MAX_LOBBY_NPC_GHOSTS = 3;

// ASSIGN_LOBBY_NPC_GHOST
executor.register('ASSIGN_LOBBY_NPC_GHOST', (state, cmd: AssignLobbyNpcGhostCommand) => {
  const { ghostId } = cmd.payload;
  const npcGhosts = state.lobbyNpcGhosts || [];

  // Max 3 lobby NPC ghosts
  if (npcGhosts.length >= MAX_LOBBY_NPC_GHOSTS) return state;
  // Already assigned
  if (npcGhosts.some((ng) => ng.ghostId === ghostId)) return state;
  // Ghost must be free
  if (!isGhostFree(state, ghostId)) return state;

  const now = Date.now();
  const entry: LobbyNpcGhost = {
    ghostId,
    assignedAt: now,
    lastScareAt: 0,
  };

  return {
    ...state,
    lobbyNpcGhosts: [...npcGhosts, entry],
    ghosts: state.ghosts.map((g) =>
      g.id === ghostId ? { ...g, role: 'lobby_staff' as GhostRole } : g,
    ),
  };
});

// UNASSIGN_LOBBY_NPC_GHOST
executor.register('UNASSIGN_LOBBY_NPC_GHOST', (state, cmd: UnassignLobbyNpcGhostCommand) => {
  const { ghostId } = cmd.payload;
  const npcGhosts = state.lobbyNpcGhosts || [];
  if (!npcGhosts.some((ng) => ng.ghostId === ghostId)) return state;

  return {
    ...state,
    lobbyNpcGhosts: npcGhosts.filter((ng) => ng.ghostId !== ghostId),
    ghosts: state.ghosts.map((g) =>
      g.id === ghostId ? { ...g, role: 'scare' as GhostRole } : g,
    ),
  };
});

// ── Random Event System ────────────────────────────────────

// RESOLVE_EVENT
executor.register('RESOLVE_EVENT', (state, cmd: ResolveEventCommand) => {
  const { eventId, choiceIndex } = cmd.payload;
  if (!state.activeEvent || state.activeEvent.eventId !== eventId) {
    return state;
  }

  const eventDef = getEventById(eventId);
  if (!eventDef) return { ...state, activeEvent: null };

  const choice = eventDef.choices[choiceIndex];
  if (!choice) return { ...state, activeEvent: null };

  // Apply choice effect (pure function)
  const effectPatch = choice.effect(state);

  // Add to history (keep last 10)
  const history = [...(state.eventHistory || []), eventId].slice(-10);

  return {
    ...state,
    ...effectPatch,
    activeEvent: null,
    eventHistory: history,
    eventCooldownUntil: Date.now() + 30000,
  };
});

// DISMISS_DEFENDED_EVENT — clears the active event after showing defense popup
executor.register('DISMISS_DEFENDED_EVENT', (state, cmd: DismissDefendedEventCommand) => {
  const { eventId } = cmd.payload;
  if (!state.activeEvent || state.activeEvent.eventId !== eventId) {
    return state;
  }
  // Effects were already applied in systems.ts; just clear the event
  return {
    ...state,
    activeEvent: null,
  };
});

// ADVANCE_TUTORIAL
executor.register('ADVANCE_TUTORIAL', (state, cmd: AdvanceTutorialCommand) => {
  return {
    ...state,
    tutorialStep: cmd.payload.step,
  };
});
